import { initContract } from '@ts-rest/core';
import Stripe from 'stripe';
import { z } from 'zod';
import { StripeAccountName } from '../types';
import {
  type StripePaywall,
  type StripeStoreProduct,
  stripePaywallRequestSchema,
  stripeProductBySkuRequestSchema,
  stripePaywallByUserRequestSchema,
} from '../types/StripePaywall';
import { StripeSubscriptionUpdateAction } from '../types/subscriptions/StripeSubscription';

export const getStripePaywallRouter = (c: ReturnType<typeof initContract>) =>
  c.router(
    {
      paywall: {
        summary: 'Gets stripe paywall (store)',
        method: 'GET',
        path: '/paywall',
        query: stripePaywallRequestSchema,
        responses: {
          200: c.type<StripePaywall>(),
        },
      },
      getPaywallByUser: {
        summary: 'Gets a stripe paywall by user',
        method: 'GET',
        path: '/user/paywall',
        query: stripePaywallByUserRequestSchema,
        headers: z.object({
          userId: z.string().optional(),
        }),
        responses: {
          200: c.type<StripePaywall>(),
          404: c.type<'Paywall not found'>(),
        },
      },
      getProductBySku: {
        summary: 'Gets a stripe product by SKU',
        method: 'GET',
        path: '/product/sku/:sku',
        headers: z.object({
          userId: z.string().optional(),
        }),
        pathParams: z.object({
          sku: z.string().min(1),
        }),
        query: stripeProductBySkuRequestSchema,
        responses: {
          200: c.type<{
            mainProduct: StripeStoreProduct;
            introOfferProduct?: StripeStoreProduct;
            stripeAccount: StripeAccountName;
          }>(),
          404: c.type<'Product not found'>(),
          500: c.noBody(),
        },
      },
      getCouponByPromoCode: {
        summary: 'Gets a stripe coupon by promo code',
        method: 'GET',
        path: '/coupon/promo-code/:promoCode',
        headers: z.object({
          userId: z.string().optional(),
        }),
        pathParams: z.object({
          promoCode: z.string().min(1),
        }),
        query: z.object({
          countryCode: z.string().min(2).max(2),
        }),
        responses: {
          200: c.type<{ coupon: Stripe.Coupon }>(),
          404: c.type<'Coupon not found'>(),
          500: c.noBody(),
        },
      },
      updateSubscription: {
        summary: 'Update stripe subscription',
        method: 'POST',
        path: '/subscription/update',
        body: c.type<{
          stripeStoreProduct?: StripeStoreProduct;
          subscriptionId: Stripe.Subscription['id'];
          action: StripeSubscriptionUpdateAction;
          updateParams?: Stripe.SubscriptionUpdateParams;
        }>(),
        responses: {
          200: c.type<{
            data: {
              updatedSubscription: Stripe.Subscription | Stripe.SubscriptionSchedule;
              paymentIntent: Stripe.PaymentIntent | null;
            };
          }>(),
          422: z.literal('unprocessable'),
        },
      },
      createSubscription: {
        summary: 'Create stripe subscription',
        method: 'POST',
        path: '/subscription/create',
        body: c.type<{
          stripeStoreProduct: StripeStoreProduct;
          metadata?: Stripe.MetadataParam;
          customerId: Stripe.Customer['id'];
          paymentMethodTypes?: string[];
        }>(),
        responses: {
          200: c.type<{
            data: {
              subscription: Stripe.Subscription | Stripe.SubscriptionSchedule;
              paymentIntentData: {
                paymentIntentId: string | null;
                clientSecret: string | null;
              };
              setupIntent: Stripe.SetupIntent | null;
            };
          }>(),
          409: c.type<{ reason: 'already_subscribed' }>(),
          422: z.literal('unprocessable'),
        },
      },
      createOneTimePurchase: {
        summary: 'Create stripe one-time purchase',
        method: 'POST',
        path: '/purchase/create',
        body: c.type<{
          stripeStoreProduct: StripeStoreProduct;
          metadata?: Stripe.MetadataParam;
          customerId: Stripe.Customer['id'];
          paymentMethodTypes?: string[];
        }>(),
        responses: {
          200: c.type<{
            data: {
              paymentIntentData: {
                paymentIntentId: string | null;
                clientSecret: string | null;
              };
            };
          }>(),
          422: z.literal('unprocessable'),
        },
      },
      createCheckoutSession: {
        summary: 'Create stripe checkout session',
        method: 'POST',
        path: '/checkout-session/create',
        query: z.object({
          countryCode: z.string().optional(),
        }),
        body: c.type<{
          checkoutEmail?: string;
          checkoutUserId?: string;
          stripeStoreProduct: StripeStoreProduct;
          language: string;
          promoCode?: string;
          paymentMethodTypes?: string[];
          freeTrialDays?: number;
          subscriptionStartDateMillis?: number;
          returnUrl: string;
          uiMode?: 'custom' | 'hosted';
          cancelUrl?: string;
          additionalMetadata?: Stripe.MetadataParam;
          stripeAccount?: StripeAccountName;
        }>(),
        responses: {
          200: c.type<{
            data: {
              clientSecret?: string;
              url?: string;
              sessionId: string;
            };
          }>(),
          422: z.literal('unprocessable'),
        },
      },
    },
    {
      pathPrefix: '/stripe',
    },
  );
